// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const REVIEWS = [
  { id: 1, name: "Ryan Miskulin", title: "CEO / Owner", tasksCompleted: 24, onTimeRate: 96, qualityScore: 4.8, collaborationScore: 4.9, overallRating: 5, trend: "stable", goals: [{ text: "Launch 3 new Forge products", progress: 60 }, { text: "Secure 5 enterprise partnerships", progress: 40 }, { text: "Hit $500K ARR", progress: 35 }] },
  { id: 2, name: "Jordan Lutz", title: "Lead Developer / Forge Master", tasksCompleted: 67, onTimeRate: 92, qualityScore: 4.9, collaborationScore: 4.7, overallRating: 5, trend: "improving", goals: [{ text: "Deploy all 17 web apps to production", progress: 88 }, { text: "Reduce build times by 40%", progress: 70 }, { text: "Implement CI/CD across all apps", progress: 55 }] },
  { id: 3, name: "Taylor Schenher", title: "CFO", tasksCompleted: 31, onTimeRate: 98, qualityScore: 4.7, collaborationScore: 4.6, overallRating: 4, trend: "stable", goals: [{ text: "Complete Q1 financial forecast", progress: 85 }, { text: "Implement new budgeting system", progress: 30 }, { text: "Reduce overhead by 15%", progress: 45 }] },
  { id: 4, name: "Des Bachynski", title: "Health Ambassador", tasksCompleted: 18, onTimeRate: 88, qualityScore: 4.3, collaborationScore: 4.5, overallRating: 4, trend: "improving", goals: [{ text: "Onboard 3 pilot health clinics", progress: 66 }, { text: "Complete beta testing feedback loop", progress: 50 }, { text: "Achieve 90% client satisfaction", progress: 72 }] },
  { id: 5, name: "Lucas Mason", title: "VP of Sales", tasksCompleted: 42, onTimeRate: 85, qualityScore: 4.4, collaborationScore: 4.8, overallRating: 4, trend: "improving", goals: [{ text: "Close $200K in Q1 deals", progress: 38 }, { text: "Build 50+ lead pipeline", progress: 55 }, { text: "Launch email drip campaigns", progress: 80 }] },
  { id: 6, name: "Lee Mehling", title: "Canadian Ambassador, Mining", tasksCompleted: 14, onTimeRate: 90, qualityScore: 4.5, collaborationScore: 4.2, overallRating: 4, trend: "stable", goals: [{ text: "Complete mining compliance audit", progress: 20 }, { text: "Visit 5 mining sites for assessments", progress: 40 }, { text: "Secure 2 mining sector partnerships", progress: 25 }] },
  { id: 7, name: "Clorice Robertson", title: "Chief Accounting Officer", tasksCompleted: 28, onTimeRate: 97, qualityScore: 4.8, collaborationScore: 4.4, overallRating: 5, trend: "stable", goals: [{ text: "Zero AR reconciliation discrepancies", progress: 95 }, { text: "Automate monthly reporting", progress: 40 }, { text: "Complete benefits renewal review", progress: 15 }] },
  { id: 8, name: "Tyson Becker", title: "NA Commodities Ambassador", tasksCompleted: 20, onTimeRate: 86, qualityScore: 4.2, collaborationScore: 4.6, overallRating: 4, trend: "improving", goals: [{ text: "Finalize Canstorr partnership agreement", progress: 50 }, { text: "Publish Q1 commodities market report", progress: 30 }, { text: "Expand commodity storage network", progress: 20 }] },
  { id: 9, name: "Tyler Hodgson", title: "Operations", tasksCompleted: 35, onTimeRate: 91, qualityScore: 4.6, collaborationScore: 4.3, overallRating: 4, trend: "stable", goals: [{ text: "Enhance Overwatch dashboard with 5 new widgets", progress: 60 }, { text: "Complete quarterly security pen test", progress: 10 }, { text: "Achieve 99.9% uptime across all apps", progress: 85 }] },
];

const PERIODS = ["Q1 2026", "Q4 2025", "Q3 2025", "Q2 2025"];

export default function PerformancePage() {
  const [selectedPeriod, setSelectedPeriod] = useState("Q1 2026");
  const [expandedMember, setExpandedMember] = useState(null);
  const [sortBy, setSortBy] = useState("name");

  const sorted = [...REVIEWS].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "tasks") return b.tasksCompleted - a.tasksCompleted;
    if (sortBy === "rating") return b.overallRating - a.overallRating;
    if (sortBy === "ontime") return b.onTimeRate - a.onTimeRate;
    return 0;
  });

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} style={{ color: i < rating ? "#eab308" : "#334155", fontSize: 16 }}>&#9733;</span>
    ));
  };

  const trendBadge = (trend) => {
    const config = { improving: { color: "#10b981", bg: "#10b98122", label: "Improving" }, stable: { color: "#3b82f6", bg: "#3b82f622", label: "Stable" }, declining: { color: "#ef4444", bg: "#ef444422", label: "Declining" } };
    const c = config[trend] || config.stable;
    return <span className="badge" style={{ background: c.bg, color: c.color }}>{c.label}</span>;
  };

  const avgTasks = Math.round(REVIEWS.reduce((s, r) => s + r.tasksCompleted, 0) / REVIEWS.length);
  const avgOnTime = Math.round(REVIEWS.reduce((s, r) => s + r.onTimeRate, 0) / REVIEWS.length);
  const avgQuality = (REVIEWS.reduce((s, r) => s + r.qualityScore, 0) / REVIEWS.length).toFixed(1);

  return (
    <div>
      <div className="page-header">
        <div><h1>Performance</h1><p>Team performance reviews and goal tracking</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", fontSize: 13 }}>
            {PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", fontSize: 13 }}>
            <option value="name">Sort by Name</option>
            <option value="tasks">Sort by Tasks</option>
            <option value="rating">Sort by Rating</option>
            <option value="ontime">Sort by On-Time %</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Avg Tasks Completed</div><div className="stat-value">{avgTasks}</div><div style={{ fontSize: 12, color: "#64748b" }}>per team member</div></div>
        <div className="stat-card"><div className="stat-label">Avg On-Time Rate</div><div className="stat-value" style={{ color: avgOnTime >= 90 ? "#10b981" : "#eab308" }}>{avgOnTime}%</div></div>
        <div className="stat-card"><div className="stat-label">Avg Quality Score</div><div className="stat-value">{avgQuality}<span style={{ fontSize: 14, color: "#64748b" }}>/5.0</span></div></div>
        <div className="stat-card"><div className="stat-label">Team Trend</div><div className="stat-value" style={{ color: "#10b981", fontSize: 20 }}>Improving</div><div style={{ fontSize: 12, color: "#64748b" }}>{REVIEWS.filter(r => r.trend === "improving").length} members improving</div></div>
      </div>

      {/* Performance Cards */}
      {sorted.map(member => (
        <div key={member.id} className="card" style={{ marginTop: 12, cursor: "pointer", borderColor: expandedMember === member.id ? "#10b981" : undefined }} onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#fff" }}>
                {member.name.split(" ").map(n => n[0]).join("")}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>{member.name}</div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>{member.title}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{member.tasksCompleted}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Tasks Done</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: member.onTimeRate >= 90 ? "#10b981" : "#eab308" }}>{member.onTimeRate}%</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>On-Time</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{member.qualityScore}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Quality</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{member.collaborationScore}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Collab</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div>{renderStars(member.overallRating)}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Overall</div>
              </div>
              {trendBadge(member.trend)}
            </div>
          </div>

          {expandedMember === member.id && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #334155" }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8", marginBottom: 12 }}>Goals - {selectedPeriod}</h4>
              {member.goals.map((goal, gi) => (
                <div key={gi} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: "#e2e8f0" }}>{goal.text}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: goal.progress >= 75 ? "#10b981" : goal.progress >= 40 ? "#eab308" : "#ef4444" }}>{goal.progress}%</span>
                  </div>
                  <div style={{ height: 6, background: "#334155", borderRadius: 3 }}>
                    <div style={{ width: goal.progress + "%", height: 6, background: goal.progress >= 75 ? "#10b981" : goal.progress >= 40 ? "#eab308" : "#ef4444", borderRadius: 3 }} />
                  </div>
                </div>
              ))}
              <div className="grid-4" style={{ marginTop: 16 }}>
                <div style={{ textAlign: "center", padding: 12, background: "#0f172a", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: "#64748b" }}>Tasks Completed</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0" }}>{member.tasksCompleted}</div>
                </div>
                <div style={{ textAlign: "center", padding: 12, background: "#0f172a", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: "#64748b" }}>On-Time Rate</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: member.onTimeRate >= 90 ? "#10b981" : "#eab308" }}>{member.onTimeRate}%</div>
                </div>
                <div style={{ textAlign: "center", padding: 12, background: "#0f172a", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: "#64748b" }}>Quality Score</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0" }}>{member.qualityScore}/5.0</div>
                </div>
                <div style={{ textAlign: "center", padding: 12, background: "#0f172a", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: "#64748b" }}>Collaboration</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0" }}>{member.collaborationScore}/5.0</div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Leaderboard */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Task Completion Leaderboard</h3></div>
        <div className="chart-bar-container">
          {[...REVIEWS].sort((a, b) => b.tasksCompleted - a.tasksCompleted).map((m, i) => (
            <div key={m.id} className="chart-bar-row">
              <span className="chart-bar-label" style={{ width: 140 }}>{i === 0 ? "1st" : i === 1 ? "2nd" : i === 2 ? "3rd" : (i + 1) + "th"} {m.name.split(" ")[0]}</span>
              <div className="chart-bar-track">
                <div className={"chart-bar-fill" + (i === 0 ? "" : i < 3 ? " blue" : " orange")} style={{ width: (m.tasksCompleted / 70 * 100) + "%" }}>{m.tasksCompleted} tasks</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
